import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

import { ReactComponent as ACTIVITY_ICON } from "../../../assets/images/Frame (28).svg";
import { siteApi } from "@/lib/api";

const FILTER_OPTIONS = ["전체", "입금", "출금"];
const REFRESH_INTERVAL_MS = 15000;
const TICKER_VISIBLE_ROWS = 9;
const TICKER_ROW_HEIGHT = 38;
const TICKER_ROW_GAP = 0;

const normalizeRows = (payload) => {
  const data = payload?.data || payload;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.list)) return data.list;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const normalizeType = (type = "") => String(type).toLowerCase();

const isWithdrawal = (type = "") => {
  const normalized = normalizeType(type);
  return (
    normalized.includes("withdraw") ||
    normalized.includes("출금") ||
    normalized.includes("debit")
  );
};

const isDeposit = (type = "") => {
  const normalized = normalizeType(type);
  return (
    normalized.includes("deposit") ||
    normalized.includes("입금") ||
    normalized.includes("credit")
  );
};

const getTypeLabel = (type) => (isWithdrawal(type) ? "출금" : "입금");

const pickAmount = (row) =>
  row?.amount ??
  row?.money ??
  row?.request_amount ??
  row?.requested_amount ??
  row?.deposit_amount ??
  row?.withdrawal_amount ??
  row?.transaction_amount;

const pickTime = (row) =>
  row?.created_at || row?.createdAt || row?.current_date_time || row?.updated_at || row?.date;

const formatAmount = (amount) => {
  const numberValue = Number(amount);
  if (!Number.isFinite(numberValue)) return "-";
  return numberValue.toLocaleString("ko-KR");
};

const formatTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
    .formatToParts(date)
    .reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {});

  return `${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
};

const maskUserValue = (value) => {
  const text = String(value || "").trim();
  if (!text) return "회원***";
  if (text.includes("*")) return text;
  if (text.length <= 2) return `${text.slice(0, 1)}*`;
  return `${text.slice(0, 2)}***${text.slice(-2)}`;
};

const maskUser = (row) =>
  maskUserValue(
    row?.user_id ||
      row?.user_unique_id ||
      row?.nick_name ||
      row?.nickname ||
      row?.username
  );

const getRowKey = (row, index, suffix = "row") =>
  `${suffix}-${pickTime(row) || "time"}-${row?.id || row?.transaction_id || index}`;

const LiveDepositWithdrawTable = () => {
  const [activeFilter, setActiveFilter] = useState(FILTER_OPTIONS[0]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    let isMounted = true;

    const fetchRows = async () => {
      const result = await siteApi.getRealtimeDepositWithdrawals({
        limit: 24,
        page: 1,
      });

      if (!isMounted) return;

      if (result?.success) {
        setRows(normalizeRows(result));
        setErrorText("");
      } else {
        setErrorText("실시간 입출금 내역을 불러오지 못했습니다.");
      }
      setIsLoading(false);
    };

    fetchRows();
    const intervalId = window.setInterval(fetchRows, REFRESH_INTERVAL_MS);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, []);

  const visibleRows = useMemo(() => {
    return rows
      .filter((row) => {
        if (activeFilter === "입금") return isDeposit(row?.type) || !isWithdrawal(row?.type);
        if (activeFilter === "출금") return isWithdrawal(row?.type);
        return true;
      })
      .slice()
      .sort((a, b) => new Date(pickTime(b) || 0) - new Date(pickTime(a) || 0))
      .slice(0, 12);
  }, [activeFilter, rows]);

  const shouldAnimateTicker = visibleRows.length > 1;
  const tickerCopyCount = shouldAnimateTicker
    ? Math.max(3, Math.ceil(TICKER_VISIBLE_ROWS / visibleRows.length) + 2)
    : 1;
  const tickerRows = shouldAnimateTicker
    ? Array.from({ length: tickerCopyCount }, () => visibleRows).flat()
    : visibleRows;
  const tickerStyle = shouldAnimateTicker
    ? {
        "--dw-ticker-distance": `${visibleRows.length * (TICKER_ROW_HEIGHT + TICKER_ROW_GAP)}px`,
        "--dw-ticker-duration": `${Math.max(visibleRows.length * 2.2, 12)}s`,
      }
    : undefined;
  const emptyText = isLoading
    ? "실시간 입출금 내역을 불러오는 중입니다."
    : errorText || "표시할 입출금 내역이 없습니다.";

  const renderTransactionRow = (row, index, isDuplicate = false) => {
    const typeLabel = getTypeLabel(row?.type);
    const typeClass = typeLabel === "입금" ? "deposit" : "withdrawal";

    return (
      <article
        className={`dw-feed-row ${typeClass}`}
        key={getRowKey(row, index, isDuplicate ? "clone" : "live")}
        aria-hidden={isDuplicate ? "true" : undefined}
      >
        <span className="dw-type">{typeLabel}</span>

        <div className="dw-user-block">
          <strong>{maskUser(row)}</strong>
        </div>

        <time className="dw-time">{formatTime(pickTime(row))}</time>

        <div className="dw-amount-wrap">
          <span className="dw-coin" aria-hidden="true">
            ₩
          </span>
          <strong>{formatAmount(pickAmount(row))}</strong>
        </div>
      </article>
    );
  };

  return (
    <StyledRealtimeDwSection>
      <div className="dw-section-header">
        <div className="dw-section-title">
          <ACTIVITY_ICON className="dw-section-icon" />
          <div className="dw-section-name">실시간 입출금</div>
        </div>

        <div className="dw-filter-group" aria-label="실시간 입출금 필터">
          {FILTER_OPTIONS.map((option) => (
            <button
              type="button"
              key={option}
              className={activeFilter === option ? "is-active" : ""}
              onClick={() => setActiveFilter(option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>

      <div className="dw-feed-shell">
        <div className="dw-table-head" aria-hidden="true">
          <span>구분</span>
          <span>회원</span>
          <span>시간</span>
          <span>금액</span>
        </div>

        {isLoading ? (
          <div className="dw-ticker-window" aria-hidden="true">
            <div className="dw-skeleton-track">
              {Array.from({ length: TICKER_VISIBLE_ROWS }).map((_, index) => (
                <div className="dw-skeleton-row" key={`dw-skeleton-${index}`}>
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
              ))}
            </div>
          </div>
        ) : visibleRows.length > 0 ? (
          <div className="dw-ticker-window" aria-live="polite">
            <div
              className={`dw-ticker-track${shouldAnimateTicker ? " is-moving" : ""}`}
              style={tickerStyle}
            >
              {tickerRows.map((row, index) =>
                renderTransactionRow(row, index, shouldAnimateTicker && index >= visibleRows.length)
              )}
            </div>
          </div>
        ) : (
          <div className="dw-empty-card">{emptyText}</div>
        )}
      </div>
    </StyledRealtimeDwSection>
  );
};

const StyledRealtimeDwSection = styled.section`
  --dw-row-height: ${TICKER_ROW_HEIGHT}px;
  --dw-row-gap: ${TICKER_ROW_GAP}px;
  --dw-head-height: 36px;
  --dw-side-padding: 16px;
  --dw-grid: 82px minmax(110px, 1fr) 132px 170px;
  --dw-window-height: calc(
    (var(--dw-row-height) * ${TICKER_VISIBLE_ROWS}) + (var(--dw-row-gap) * ${TICKER_VISIBLE_ROWS - 1})
  );

  display: flex;
  flex-direction: column;
  gap: 12px;

  .dw-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
  }

  .dw-section-title {
    display: flex;
    align-items: center;
    min-width: 0;
    gap: 10px;
  }

  .dw-section-icon {
    width: 22px;
    height: 22px;
    flex: 0 0 auto;
    color: var(--crypto-green);
    filter: drop-shadow(var(--crypto-green-glow) 0 0 5px);
  }

  .dw-section-icon *:not([fill="none"]) {
    fill: currentColor;
  }

  .dw-section-name {
    color: #ffffff;
    font-size: 17px;
    line-height: 1.25;
    font-weight: 700;
  }

  .dw-filter-group {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px;
    border-radius: 10px;
    background: rgba(8, 22, 29, 0.76);
    border: 1px solid var(--crypto-border-soft);
  }

  .dw-filter-group button {
    height: 30px;
    min-width: 46px;
    border-radius: 8px;
    color: #8f96a8;
    font-size: 12px;
    line-height: 1;
    font-weight: 500;
    transition: color 0.16s ease, background 0.16s ease;
  }

  .dw-filter-group button:hover {
    color: #ffffff;
  }

  .dw-filter-group button.is-active {
    color: var(--crypto-green);
    background: rgba(89, 244, 119, 0.14);
    box-shadow: inset 0 0 0 1px rgba(89, 244, 119, 0.08);
  }

  .dw-feed-shell {
    position: relative;
    height: calc(var(--dw-head-height) + var(--dw-window-height));
    overflow: hidden;
    border-radius: 10px;
    border: 1px solid var(--crypto-border);
    background:
      linear-gradient(180deg, rgba(29, 62, 74, 0.7), rgba(13, 31, 40, 0.94)),
      var(--crypto-surface-2);
    box-shadow: 0 14px 26px rgba(0, 0, 0, 0.16), inset 0 1px 0 rgba(255, 255, 255, 0.025);
  }

  .dw-table-head {
    position: relative;
    z-index: 2;
    display: grid;
    grid-template-columns: var(--dw-grid);
    align-items: center;
    height: var(--dw-head-height);
    padding: 0 var(--dw-side-padding);
    background: rgba(7, 19, 27, 0.78);
    border-bottom: 1px solid var(--crypto-border-soft);
  }

  .dw-table-head span {
    color: #9da7bc;
    font-size: 12px;
    line-height: 1;
    font-weight: 500;
    white-space: nowrap;
  }

  .dw-table-head span:last-child {
    text-align: right;
  }

  .dw-ticker-window {
    height: var(--dw-window-height);
    overflow: hidden;
  }

  .dw-ticker-track {
    display: flex;
    flex-direction: column;
    gap: var(--dw-row-gap);
    padding: 0;
    will-change: transform;
  }

  .dw-skeleton-track {
    display: flex;
    flex-direction: column;
  }

  .dw-ticker-track.is-moving {
    animation: cryptoDwTickerMove var(--dw-ticker-duration, 9.4s) linear infinite;
  }

  .dw-feed-row {
    position: relative;
    display: grid;
    grid-template-columns: var(--dw-grid);
    align-items: center;
    height: var(--dw-row-height);
    min-height: var(--dw-row-height);
    padding: 0 var(--dw-side-padding);
    overflow: hidden;
    background: rgba(10, 25, 34, 0.62);
    border-bottom: 1px solid var(--crypto-border-soft);
  }

  .dw-skeleton-row {
    display: grid;
    grid-template-columns: var(--dw-grid);
    align-items: center;
    height: var(--dw-row-height);
    padding: 0 var(--dw-side-padding);
    background: rgba(10, 25, 34, 0.58);
    border-bottom: 1px solid var(--crypto-border-soft);
  }

  .dw-skeleton-row:nth-child(2n) {
    background: rgba(30, 57, 68, 0.58);
  }

  .dw-skeleton-row span {
    height: 11px;
    max-width: 72%;
    border-radius: 999px;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0.055),
      rgba(255, 255, 255, 0.11),
      rgba(255, 255, 255, 0.055)
    );
    background-size: 220% 100%;
    animation: cryptoSkeletonShimmer 1.35s ease-in-out infinite;
  }

  .dw-skeleton-row span:last-child {
    justify-self: end;
  }

  .dw-feed-row:nth-child(2n) {
    background: rgba(30, 57, 68, 0.62);
  }

  .dw-type {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    width: fit-content;
    height: auto;
    min-width: 0;
    border-radius: 0;
    color: var(--crypto-green);
    background: transparent;
    font-size: 11px;
    line-height: 1;
    font-weight: 500;
    box-shadow: none;
  }

  .dw-feed-row.withdrawal .dw-type {
    color: #ffb86b;
    background: transparent;
    box-shadow: none;
  }

  .dw-user-block {
    min-width: 0;
    display: flex;
    flex-direction: column;
  }

  .dw-user-block strong {
    max-width: 210px;
    overflow: hidden;
    color: #d4dae6;
    font-size: 12px;
    line-height: 1;
    font-weight: 500;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dw-time {
    color: #9ca6bb;
    font-size: 12px;
    line-height: 1;
    font-weight: 400;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  .dw-amount-wrap {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 0;
    gap: 5px;
    color: #ffffff;
  }

  .dw-coin {
    position: relative;
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #0d1510;
    background: linear-gradient(145deg, #dfffe7, var(--crypto-green) 46%, #14b451);
    box-shadow: 0 4px 10px rgba(89, 244, 119, 0.16), inset 0 1px 1px rgba(255, 255, 255, 0.46);
    font-size: 10px;
    line-height: 1;
    font-weight: 600;
  }

  .dw-coin::after {
    content: "";
    position: absolute;
    inset: 2px;
    border-radius: 50%;
    border: 1px solid rgba(13, 21, 16, 0.18);
  }

  .dw-feed-row.withdrawal .dw-coin {
    color: #201408;
    background: linear-gradient(145deg, #fff0ca, #ffb86b 48%, #e47a28);
    box-shadow: 0 4px 10px rgba(255, 184, 107, 0.16), inset 0 1px 1px rgba(255, 255, 255, 0.46);
  }

  .dw-amount-wrap strong {
    min-width: 0;
    overflow: hidden;
    color: #d5dce8;
    font-size: 12px;
    line-height: 1;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
    text-align: right;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .dw-empty-card {
    height: var(--dw-window-height);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #8e95a8;
    text-align: center;
    padding: 18px;
    font-size: 13px;
    font-weight: 500;
  }

  @keyframes cryptoDwTickerMove {
    to {
      transform: translateY(calc(var(--dw-ticker-distance, 328px) * -1));
    }
  }

  @keyframes cryptoSkeletonShimmer {
    0% {
      background-position: 100% 0;
    }
    100% {
      background-position: -120% 0;
    }
  }

  @media (max-width: 768px) {
    --dw-side-padding: 9px;
    --dw-head-height: 34px;
    --dw-grid: 46px minmax(58px, 1fr) 66px minmax(88px, auto);

    .dw-section-header {
      align-items: flex-start;
      flex-direction: column;
    }

    .dw-section-name {
      font-size: 16px;
    }

    .dw-filter-group {
      width: 100%;
    }

    .dw-filter-group button {
      flex: 1;
    }

    .dw-table-head span {
      font-size: 10px;
    }

    .dw-feed-row {
      column-gap: 3px;
    }

    .dw-type {
      min-width: 0;
      height: auto;
      border-radius: 0;
      font-size: 10px;
    }

    .dw-user-block strong {
      max-width: 70px;
      font-size: 10px;
    }

    .dw-time {
      font-size: 10px;
    }

    .dw-amount-wrap {
      gap: 4px;
    }

    .dw-coin {
      width: 15px;
      height: 15px;
      font-size: 9px;
    }

    .dw-amount-wrap strong {
      font-size: 11px;
    }
  }
`;

export default LiveDepositWithdrawTable;
